import { useMemo, useState, useEffect, useRef } from "react";
import { TextField, IconButton, InputAdornment } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const MINIMUM_PASSWORD = 6;

export function PasswordInput({
  value,
  onChange,
  onBlur,
  onValidationChange,
  fullWidth = true,
  label = "Password",
  autoComplete = "current-password",
  enforceMinLength = false,
  validate,
  ...props
}) {
  const [touched, setTouched] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const onValidationChangeRef = useRef(onValidationChange);
  useEffect(() => {
    onValidationChangeRef.current = onValidationChange;
  }, [onValidationChange]);

  const builtInError = useMemo(() => {
    if (!touched) return "";
    const v = value.trim();
    if (!v) return "Password is required.";
    if (enforceMinLength && MINIMUM_PASSWORD > 0 && v.length < MINIMUM_PASSWORD) {
      return `Password must be at least ${MINIMUM_PASSWORD} characters.`;
    }
    return "";
  }, [value, touched, enforceMinLength]);

  const customError = useMemo(
    () => (validate ? validate(value) : ""),
    [validate, value]
  );

  const mergedError = useMemo(() => {
    if (!touched) return "";
    return builtInError || customError;
  }, [touched, builtInError, customError]);

  const isValid = useMemo(() => {
    const v = value.trim();
    const baseOk =
      v && (!enforceMinLength || MINIMUM_PASSWORD === 0 || v.length >= MINIMUM_PASSWORD);
    return Boolean(baseOk && !customError);
  }, [value, customError, enforceMinLength]);

  // only fire when validity changes (not when callback identity changes)
  useEffect(() => {
    onValidationChangeRef.current?.(isValid);
  }, [isValid]);

  const handleChange = (ev) => {
    onChange?.(ev.target.value);
  };

  const handleBlur = (ev) => {
    setTouched(true);
    const trimmed = ev.target.value.trim();
    onChange?.(trimmed);
    onBlur?.(trimmed);
  };

  return (
    <TextField
      type={showPassword ? "text" : "password"}
      label={label}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      error={Boolean(mergedError)}
      helperText={mergedError}
      autoComplete={autoComplete}
      required
      fullWidth={fullWidth}
      variant="outlined"
      aria-label="Password"
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((prev) => !prev)}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
                size="small"
              >
                {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
      {...props}
    />
  );
}
